<template>
  <div v-if="mandatoryCount > 0 && !mandatorySelected">
    <h4 class="mt-4">
      {{ $t('configurator.headings.lettering_required') }}
    </h4>
    <div class="text-center mt-3">
      <button
        type="button"
        class="btn btn-primary"
        :disabled="availableRefinements.length === 0"
        @click="showAddMandatoryModal">
        {{ $t('configurator.buttons.add_lettering') }}
      </button>
    </div>
  </div>
  <h4 class="mt-4" title="Grafische Gestaltung">
	  {{ $t('configurator.headings.graphic_design') }}
	</h4>
  <table class="table table-condensed table-sm">
    <thead>
      <tr>
        <th>{{ $t('configurator.tables.columns.surface') }}</th>
        <th>{{ $t('configurator.tables.columns.print_article') }}</th>
        <th class="text-right" v-if="false">{{ $t('configurator.tables.columns.price_per_piece') }}</th>
        <th>{{ $t('configurator.tables.columns.lettering_graphic_file') }}</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      <tr v-if="shoppingCart.booth_configuration.length === 0">
        <td colspan="3" class="text-center">
          <i>{{ $t('configurator.labels.no_surfaces_selected') }}</i>
        </td>
      </tr>
      <template v-for="group,gIndex in groupedConfigurations" :key="gIndex">
        <configurator-booth-refinement-group v-if="group.length > 1"
            :group="group"
            @show="showArticleModal">
        </configurator-booth-refinement-group>
        <configuration-booth-refinement v-else
          :configuration="group[0]"
          @show="showArticleModal">
        </configuration-booth-refinement>
      </template>
    </tbody>
  </table>
  <div class="text-center">
    <button
      type="button"
      class="btn btn-primary"
      :disabled="availableRefinements.length === 0"
      @click="showAddModal">
      {{ $t('configurator.buttons.add_graphic_design') }}
    </button>
  </div>
  <AddMandatoryBoothRefinementModal v-if="addMandatoryModalActive"
    :form="refinementForm"
    :available-refinements="availableRefinements"
    @close="hideAddMandatoryModal"
    @submit="addMandatoryRefinements" />

  <AddBoothRefinementModal v-if="addModalActive"
    :booth="shoppingCart.booth"
    :booth-type="shoppingCart.booth_type.type"
    :configurator="true"
    :form="refinementForm"
    :available-refinements="availableRefinements"
    :mandatory-mode="mandatoryMode"
    :width="shoppingCart.width"
    :depth="shoppingCart.depth"
    @close="hideAddModal"
    @submit="addRefinements" />
  
  <article-modal v-if="articleModalActive"
    :article="selectedArticle"
    @close="hideArticleModal">
  </article-modal>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useMainStore } from '@/store'
import Form from '@/classes/Form'
import { useI18n } from 'vue-i18n'
import moment from 'moment'
import { filterAvailableRefinements } from '@/utils/boothRefinements'
import AddMandatoryBoothRefinementModal from '@/components_new/Configurator/AddMandatoryBoothRefinementModal.vue'

const { t } = useI18n({ useScope: 'global' })
const store = useMainStore()
// define props
const props = defineProps({
  mandatorySelected: {
    type: Boolean,
    default: false
  },
  mandatoryCount: {
    type: Number,
    default: 0
  },
})
// define reactive props
const addModalActive = ref(false)
const addMandatoryModalActive = ref(false)
const articleModalActive = ref(false)
const mandatoryMode = ref(false)
const dropzoneFile = ref('')
const refinementForm = reactive(new Form({
  refinement: null, // deprecated
  refinements: [],
  is_logo_print: false,
  spanning_mode: null,
  label: '',
  article: null,
  file: null,
  is_seamless_print: false,
}))
const selectedArticle = ref(null)
// define computed props
const locale = computed(() => store.settings.locale)
const basket = computed(() => store.settings.basket)
const shoppingCart = computed(() => store.settings.shopping_cart)
const disabledBoothRefinementArticleIds = computed(() => store.availability.disabled_booth_refinement_ids)
const bookableArticleIds = computed(() => store.availability.bookable_article_ids)
const includedCharacters = computed(() => store.tmp.characters_inclusive)

const availableRefinements = computed(() => {
  return filterAvailableRefinements(
		shoppingCart.value,
		disabledBoothRefinementArticleIds.value,
		mandatoryMode.value,
    bookableArticleIds.value
	)
})

const groupedConfigurations = computed(() => {
  return [
    ...shoppingCart.value.booth_configuration
      .reduce((a, b) => a.set(b.group, [...a.get(b.group) || [], b]), new Map())
      .values()
  ]
})
// define methods
const addRefinements = async () => {
  let index = 0
  const group = moment().unix()

  let totalArea = 0
  if (refinementForm.is_seamless_print) {
    totalArea = refinementForm.refinements.reduce((a,b) => a + (parseFloat(b.height) * parseFloat(b.width)), 0)
  }

  await refinementForm.refinements.forEach((refinement) => {
    addRefinement(refinement, refinementForm, index, group, totalArea)
    index++
  })
  
  refinementForm.resetToOriginal()
  dropzoneFile.value = ''
  hideAddModal()
  hideAddMandatoryModal()
}

const addRefinement = (refinement, form, rIndex, potentialGroup, totalArea = 0) => {
  const clonedRefinement = _.cloneDeep(refinement)
  // spanning.value is not a reference, it's just a boolean value
  const group = (form.spanning_mode && form.spanning_mode.value) ? potentialGroup : clonedRefinement.id
  const configuration = {
    id: clonedRefinement.id,
    booth_refinement: clonedRefinement,
    is_logo_print: form.is_logo_print,
    is_seamless_print: form.is_seamless_print,
    article_id: form.article.article.id,
    label: form.label,
    file: form.file,
    group: (form.is_seamless_print) ? group : clonedRefinement.id
  }
  shoppingCart.value.addBoothConfiguration(configuration)

  // find print article 
  const index = basket.value.selectedArticles.findIndex((article) => {
    return article.id === configuration.article_id
  })
  if (index === -1) {
    console.warn('lettering article not found')
    return
  }
  const clonedArticle = _.cloneDeep(basket.value.selectedArticles[index])
  if (configuration.is_logo_print) {
    clonedArticle.count = 1
  } else {
    if (form.spanning_mode && form.spanning_mode.value && rIndex > 0) {
      clonedArticle.count = 0
      clonedArticle.count_after_inclusive = 0
      clonedArticle.inclusive = 0
    } else {
      clonedArticle.count = configuration.label
        .trim()
        .replace(/ /g, '')
        .length
      if (clonedArticle.count <= includedCharacters.value) {
        clonedArticle.inclusive = clonedArticle.count
      } else {
        clonedArticle.inclusive = includedCharacters.value
      }
      clonedArticle.count_after_inclusive = clonedArticle.count - clonedArticle.inclusive
    }
  }
  if(form.is_seamless_print) {
    clonedArticle.is_seamless_print = 1
    clonedArticle.name = clonedArticle.name + ' (nahtloser Druck)'
    clonedArticle.name_en = clonedArticle.name_en + ' (seamless print)'
    if (rIndex === 0) {
      clonedArticle.count = totalArea
    } else {
      clonedArticle.count = 0
    }
  }

  if(form.article.is_included) {
    clonedArticle.print_inclusive = 1
    clonedArticle.inclusive = 1
    clonedArticle.price = 0
    clonedArticle.total = 0
    clonedArticle.totalNet = 0
    clonedArticle.totalVat = 0
    clonedArticle.name = clonedArticle.name + ' (inklusive)'
    clonedArticle.name_en = clonedArticle.name_en + ' (inclusive)'
  }
  shoppingCart.value.addBoothRefinementArticle(clonedArticle, clonedRefinement)
}

const removeRefinement = (configuration) => {
  shoppingCart.value.removeBoothConfiguration(configuration.id)
  shoppingCart.value.removeBoothRefinementArticle(configuration.id)
}

const showAddModal = () => {
  addModalActive.value = true
  if (availableRefinements.value.length === 1) {
    refinementForm.refinements = [availableRefinements.value[0]]
  } else {
    //refinementForm.refinements = availableMandatoryRefinements.value
  }

  refinementForm.refinements = [];
}

const hideAddModal = () => {
  addModalActive.value = false
  refinementForm.resetToOriginal()
}

const showAddMandatoryModal = () => {
  mandatoryMode.value = true
  addMandatoryModalActive.value = true
}

const hideAddMandatoryModal = () => {
  addMandatoryModalActive.value = false
  mandatoryMode.value = false
}

const showArticleModal = (article) => {
  selectedArticle.value = article
  articleModalActive.value = true
}

const hideArticleModal = (article) => {
  articleModalActive.value = false
  selectedArticle.value = null
}

const addMandatoryRefinements = async (forms) => {
  let index = 0
  const group = moment().unix()
  await forms.forEach((form) => {
    addRefinement(form.refinements[0], form, index, group)
    index++
  })
  
  hideAddMandatoryModal()
}
</script>